import { Bank, CheckCircle, WarningCircle, ChartLine, ArrowsHorizontal, Clock, Coins } from "@phosphor-icons/react";

const SimpleRow = ({ label, value }) => (
  <div className="flex justify-between items-start py-2.5 border-b border-zinc-800/50 last:border-b-0">
    <span className="text-xs text-zinc-400">{label}</span>
    <span className="text-xs text-zinc-100 font-medium text-right max-w-[60%]">{value}</span>
  </div>
);

const PointList = ({ items, tone = "good" }) => (
  <ul className="space-y-2">
    {items.map((it, i) => (
      <li key={i} className="flex items-start gap-2 text-xs text-zinc-300 leading-relaxed">
        {tone === "good"
          ? <CheckCircle size={13} weight="fill" className="text-emerald-400 shrink-0 mt-0.5" />
          : <WarningCircle size={13} weight="fill" className="text-red-400 shrink-0 mt-0.5" />}
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

export default function RetireIndexFunds() {
  return (
    <div className="p-5 max-w-[1400px] space-y-6" data-testid="retire-index-funds-page">
      {/* Hero */}
      <div className="bg-zinc-900/50 border border-zinc-800/80 rounded-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-sm bg-emerald-900/20 flex items-center justify-center">
            <ChartLine size={20} weight="bold" className="text-emerald-400" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-bold text-zinc-50">Index Funds</h2>
            <p className="text-xs text-zinc-400">The simplest way to own a piece of every major company at once</p>
          </div>
        </div>
        <p className="text-sm text-zinc-300 leading-relaxed">
          An index fund is a basket of investments that automatically owns every company in a specific list (called an "index"). When you buy one share of an S&amp;P 500 index fund, you own a tiny slice of all 500 of America's biggest companies. No stock picking. No guessing. Just the whole market for a near-zero fee.
        </p>
      </div>

      {/* Quick facts */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-5">
        <h3 className="text-sm font-heading font-bold text-zinc-100 mb-3 flex items-center gap-2">
          <Clock size={14} weight="bold" className="text-emerald-400" /> Quick Facts
        </h3>
        <SimpleRow label="Who can buy them" value="Anyone with a brokerage account" />
        <SimpleRow label="Minimum investment" value="$1 (fractional shares) — most have no minimum" />
        <SimpleRow label="Average expense ratio" value="0.03% – 0.20% (vs ~1% for actively managed funds)" />
        <SimpleRow label="Historical return (S&P 500)" value="~10% per year on average since 1957" />
        <SimpleRow label="Risk level" value="Moderate to high (it IS the stock market)" />
        <SimpleRow label="Tax treatment" value="Depends on the account you hold it in" />
      </div>

      {/* Popular index funds */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-5">
        <h3 className="text-sm font-heading font-bold text-zinc-100 mb-3 flex items-center gap-2">
          <Coins size={14} weight="bold" className="text-amber-400" /> Popular Index Funds &amp; ETFs
        </h3>
        <div className="space-y-2 text-xs text-zinc-300 leading-relaxed">
          <p><span className="font-bold text-zinc-50">VOO / SPY / IVV</span> — Track the S&amp;P 500. The 500 biggest US companies. Most popular index in the world.</p>
          <p><span className="font-bold text-zinc-50">VTI</span> — Total US Stock Market. Owns nearly every public US company (~4,000 stocks).</p>
          <p><span className="font-bold text-zinc-50">QQQ</span> — Nasdaq-100. Top 100 tech-heavy companies (Apple, Microsoft, NVIDIA, etc.).</p>
          <p><span className="font-bold text-zinc-50">VXUS</span> — Total International. Every non-US developed and emerging market.</p>
          <p><span className="font-bold text-zinc-50">VT</span> — Total World Stock. US + international in one fund.</p>
          <p><span className="font-bold text-zinc-50">BND</span> — Total Bond Market. For when you want stability over growth.</p>
        </div>
      </div>

      {/* Why they win */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-5">
        <h3 className="text-sm font-heading font-bold text-zinc-100 mb-3 flex items-center gap-2">
          <ArrowsHorizontal size={14} weight="bold" className="text-blue-400" /> Why They Beat Most Hedge Funds
        </h3>
        <p className="text-xs text-zinc-300 leading-relaxed mb-2">
          Warren Buffett famously bet $1 million that an S&amp;P 500 index fund would beat the best hedge fund managers over 10 years. He won by a landslide.
        </p>
        <p className="text-xs text-zinc-300 leading-relaxed">
          Over any 20-year period, ~85% of actively managed funds underperform their benchmark index. The reason is simple: high fees + emotional decisions + tax drag eat the returns. Index funds remove all three.
        </p>
      </div>

      {/* Pros / Cons */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-emerald-900/10 border border-emerald-800/30 rounded-sm p-5">
          <h3 className="text-sm font-heading font-bold text-emerald-300 mb-3">Pros</h3>
          <PointList items={[
            "Near-zero fees (0.03% on VOO = $3 per $10,000/year).",
            "Instant diversification — own hundreds of companies in one buy.",
            "Beats ~85% of actively managed funds over 20 years.",
            "Tax-efficient (low turnover = fewer taxable events).",
            "Brain-dead simple. No research required.",
          ]} />
        </div>
        <div className="bg-red-900/10 border border-red-800/30 rounded-sm p-5">
          <h3 className="text-sm font-heading font-bold text-red-300 mb-3">Cons</h3>
          <PointList tone="bad" items={[
            "You'll never beat the market — only match it.",
            "Goes down when the market goes down (no defense in crashes).",
            "Boring. No exciting stock picks to brag about.",
            "Sector-specific indexes can be concentrated (e.g., QQQ = 50% tech).",
            "International index funds have lagged the US for 15+ years.",
          ]} />
        </div>
      </div>

      {/* Bottom line */}
      <div className="bg-amber-900/10 border border-amber-800/30 rounded-sm p-5">
        <h3 className="text-sm font-heading font-bold text-amber-300 mb-2 flex items-center gap-2">
          <Bank size={14} weight="bold" /> Bottom Line
        </h3>
        <p className="text-xs text-zinc-300 leading-relaxed">
          If you do nothing else with your portfolio, putting your retirement contributions into a 3-fund portfolio (US stock index + International index + Bond index) will beat the vast majority of investors over a 30-year career. Buffett's instruction to his trustee for his wife's money: "Put 90% in an S&amp;P 500 index fund, and 10% in short-term government bonds."
        </p>
      </div>
    </div>
  );
}
